import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/admin/AuthCallback";
import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import History from "@/pages/public/History";
import Leadership from "@/pages/public/Leadership";
import Services from "@/pages/public/Services";
import ChurchFamily from "@/pages/public/ChurchFamily";
import Ministries from "@/pages/public/Ministries";
import Events from "@/pages/public/Events";
import Announcements from "@/pages/public/Announcements";
import Gallery from "@/pages/public/Gallery";
import Contact from "@/pages/public/Contact";
import AdminLogin from "@/pages/admin/AdminLogin";
import ForgotPassword from "@/pages/admin/ForgotPassword";
import ResetPassword from "@/pages/admin/ResetPassword";
import AdminLayout from "@/components/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import FamilyDirectory from "@/pages/admin/FamilyDirectory";
import FamilyDetail from "@/pages/admin/FamilyDetail";
import Members from "@/pages/admin/Members";
import CrudPage from "@/components/admin/CrudPage";
import { ADMIN_CONFIGS } from "@/components/admin/adminConfigs";
import AboutAdmin from "@/pages/admin/AboutAdmin";
import Ledger from "@/pages/admin/Ledger";
import Birthdays from "@/pages/admin/Birthdays";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import MessagesAdmin from "@/pages/admin/MessagesAdmin";
import AuditLog from "@/pages/admin/AuditLog";
import AccessManagement from "@/pages/admin/AccessManagement";
import Reports from "@/pages/admin/Reports";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-navy border-t-gold" aria-label="Loading" />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect the OAuth session_id synchronously during render (not in useEffect)
  // to avoid race conditions with the auth check.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/history" element={<History />} />
              <Route path="/leadership" element={<Leadership />} />
              <Route path="/services" element={<Services />} />
              <Route path="/church-family" element={<ChurchFamily />} />
              <Route path="/ministries" element={<Ministries />} />
              <Route path="/events" element={<Events />} />
              <Route path="/announcements" element={<Announcements />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route
              path="/admin"
              element={
                <Protected>
                  <AdminLayout />
                </Protected>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="families" element={<FamilyDirectory />} />
              <Route path="families/:familyId" element={<FamilyDetail />} />
              <Route path="members" element={<Members />} />
              <Route path="events" element={<CrudPage {...ADMIN_CONFIGS.events} />} />
              <Route path="announcements" element={<CrudPage {...ADMIN_CONFIGS.announcements} />} />
              <Route path="gallery" element={<CrudPage {...ADMIN_CONFIGS.gallery} />} />
              <Route path="history" element={<CrudPage {...ADMIN_CONFIGS.history} />} />
              <Route path="leadership" element={<CrudPage {...ADMIN_CONFIGS.leadership} />} />
              <Route path="ministries" element={<CrudPage {...ADMIN_CONFIGS.ministries} />} />
              <Route path="services" element={<CrudPage {...ADMIN_CONFIGS.services} />} />
              <Route path="about" element={<AboutAdmin />} />
              <Route path="ledger" element={<Ledger />} />
              <Route path="birthdays" element={<Birthdays />} />
              <Route path="messages" element={<MessagesAdmin />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="access" element={<AccessManagement />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<SettingsAdmin />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
